import {useQuery} from "@apollo/client";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import some from "lodash/some";
import {useEffect, useMemo, useState} from "react";
import {Helmet} from "react-helmet-async";
import {useBeforeUnload, useBlocker} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SortingDirection} from "@/components/table/Table.types.ts";
import {METRICS_EXPLORER} from "@/lib/featureFlags.ts";
import {DashboardStatsResult, GQL_DASHBOARD_STATS} from "@/lib/queries/website.ts";
import {DashboardDefinition} from "@/lib/types/website.ts";
import {CheckUnsavedModal} from "@/pages/checks/update/sections/CheckUnsavedModal.tsx";
import {DashboardActionBar} from "@/pages/dashboard/display/components/dashboardActionBar/DashboardActionBar.tsx";
import {DashboardAlerts} from "@/pages/dashboard/display/components/DashboardAlerts.tsx";
import {DashboardCharts} from "@/pages/dashboard/display/components/DashboardCharts.tsx";
import {DashboardChecks} from "@/pages/dashboard/display/components/DashboardChecks.tsx";
import {DashboardMetrics} from "@/pages/dashboard/display/components/DashboardMetrics.tsx";
import {dashboardDefinitionToStatsInput} from "@/pages/dashboard/display/dashboardUtils.ts";
import {EditDashboardModal} from "@/pages/dashboard/edit/EditDashboardModal.tsx";

type DashboardDisplayProps = {
  dashboardDef: DashboardDefinition;
  isNew?: boolean;
};

const definitionsMatch = (dashboardDef: DashboardDefinition, localDashboardDef: DashboardDefinition) => {
  const matches = Object.keys(dashboardDef).map((key: string) => {
    const dashKey = key as keyof DashboardDefinition;

    if (Array.isArray(dashboardDef[dashKey]) && Array.isArray(localDashboardDef[dashKey])) {
      return !isEqual(omit(dashboardDef[dashKey], ["__typename"]), omit(localDashboardDef[dashKey], ["__typename"]));
    } else {
      return dashboardDef[dashKey] !== localDashboardDef[dashKey];
    }
  });

  return !some(matches, Boolean);
};

export const DashboardDisplay = ({dashboardDef, isNew = false}: DashboardDisplayProps) => {
  const [servicesToShow, setServicesToShow] = useState<number>(dashboardDef.services_num_to_show);
  const [localDashboardDef, _setLocalDashboardDef] = useState<DashboardDefinition>(dashboardDef);
  const [refreshRate, setRefreshRate] = useState<number | undefined>(undefined);
  const [showEditDashboardModal, setShowEditDashboardModal] = useState<boolean>(isNew);
  const {userHasFeature} = useCurrentUserContext();

  const setLocalDashboardDef = (dashboardDef: DashboardDefinition) => {
    _setLocalDashboardDef(dashboardDef);
    setServicesToShow(
      dashboardDef.services_num_to_show > servicesToShow ? dashboardDef.services_num_to_show : servicesToShow
    );
  };

  const unsaved = useMemo(() => !definitionsMatch(dashboardDef, localDashboardDef), [dashboardDef, localDashboardDef]);

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const blocker = useBlocker(unsaved && !showUnsavedModal && !justSaved);

  useBeforeUnload((event) => {
    if (unsaved) {
      event.preventDefault();
      return "You have unsaved changes. Are you sure you want to discard your changes?";
    }
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (unsaved) {
      setShowUnsavedModal(true);
    } else {
      blocker.proceed();
    }
  }, [blocker, unsaved]);

  const {data, loading, previousData, refetch} = useQuery<DashboardStatsResult>(GQL_DASHBOARD_STATS, {
    variables: {
      dashboard_def: dashboardDefinitionToStatsInput(localDashboardDef),
      limit: servicesToShow,
    },
    pollInterval: refreshRate ? refreshRate * 1000 : 0,
  });

  const currentData = loading ? previousData : data;

  if (!currentData) {
    return <LoadingContainer text="Loading Dashboard..." />;
  }

  return (
    <ContentContainer
      title={
        <>
          {localDashboardDef.name}
          {unsaved && <span className="text-warning ps-1">(unsaved)</span>}
        </>
      }
      actionsBar={
        <DashboardActionBar
          unsaved={unsaved}
          dashboardDef={localDashboardDef}
          dashboardDefs={currentData.dashboardDefs}
          refreshRate={refreshRate}
          setRefreshRate={setRefreshRate}
          doRefresh={() => refetch()}
          setShowEditDashboardModal={setShowEditDashboardModal}
          onBeforeSaveNavigate={() => setJustSaved(true)}
        />
      }
      showStatusBanner={true}
    >
      <Helmet>
        <title>{localDashboardDef.name}</title>
      </Helmet>
      <div className="mb-5">
        <div className={"d-flex flex-column gap-4"}>
          {localDashboardDef.metrics_show_section && currentData.dashboardMetrics.global_metrics && (
            <DashboardMetrics metrics={currentData.dashboardMetrics.global_metrics} />
          )}
          {localDashboardDef.services_show_section && currentData.dashboardServiceInfo.service_info && (
            <DashboardChecks
              dashboardDef={localDashboardDef}
              services={currentData.dashboardServiceInfo.service_info}
              servicesToShow={servicesToShow}
              servicesTotal={currentData.dashboardServiceInfo.service_total}
              setServicesToShow={setServicesToShow}
              performanceMetrics={currentData.dashboardServiceInfo.service_performance_metrics}
              serviceDefinitions={currentData.serviceDefinitions}
            />
          )}
          {localDashboardDef.charts_show_section && userHasFeature(METRICS_EXPLORER) && (
            <DashboardCharts charts={localDashboardDef.charts} refreshRate={refreshRate} />
          )}
          {localDashboardDef.alerts_show_section && currentData.dashboardMetrics.latest_outages && (
            <DashboardAlerts
              alerts={currentData.dashboardMetrics.latest_outages}
              sortColumn={"-created_at"}
              sortDirection={SortingDirection.Asc}
              enableSorting={false}
            />
          )}
        </div>
        <CheckUnsavedModal
          show={showUnsavedModal}
          onCancel={() => {
            if (blocker.state === "blocked") blocker.reset();
            setShowUnsavedModal(false);
          }}
          onConfirm={() => {
            if (blocker.state === "blocked") blocker.proceed();
            setShowUnsavedModal(false);
          }}
        />
        <EditDashboardModal
          dashboardDef={localDashboardDef}
          dashboardDefs={currentData.dashboardDefs}
          tags={currentData.tags}
          monitoringServiceTypes={currentData.monitoringServiceTypes}
          show={showEditDashboardModal}
          close={() => setShowEditDashboardModal(false)}
          cancel={() => {
            setShowEditDashboardModal(false);
          }}
          updateDashboardDef={(dashboardDef: DashboardDefinition) => setLocalDashboardDef(dashboardDef)}
        />
      </div>
    </ContentContainer>
  );
};
